import { useQuery } from "@tanstack/react-query"
import {
  getAccountStatuses,
  getAccountStatusReasons,
  getOrderReasons,
  getOrderStatuses,
  getRevenueClasses,
  getStates,
  getContactTypes,
  getContactDispositions,
  getPhoneNumberTypes,
  getContactLanguages,
  getDefaultDispositions,
  getResubmitDispositions,
  getRejectStatus,
  getBusinessTitles,
  getAccountHolderRelationship,
  getCaseAssignmentGroups,
} from "./sharedMethods"
import {
  accountStatusKeys,
  statusReasonKeys,
  orderStatusKeys,
  orderStatusReasonsKeys,
  revenueClassesKeys,
  stateKeys,
  contactTypesKeys,
  contactDispositionsKeys,
  phoneNumberTypesKeys,
  contactLanguagesKeys,
  defaultDispositionsKeys,
  resubmitDispositionsKeys,
  rejectStatusKeys,
  businessTitlesKeys,
  accountHolderRelationshipKeys,
  caseAssignmentGroupsKeys,
} from "./queryKeys"
import type { IContactDisposition } from "@apis/prospects/types"
import { transformContactDispositionData } from "./dataTransformations"
import { sortLabels } from "@utils/formatters"

export const useGetAccountStatusesQuery = () => {
  return useQuery({
    queryKey: accountStatusKeys.lists(),
    queryFn: getAccountStatuses,
  })
}
export const useGetAccountStatusReasonsQuery = () => {
  return useQuery({
    queryKey: statusReasonKeys.lists(),
    queryFn: getAccountStatusReasons,
  })
}
export const useGetOrderStatusesQuery = () => {
  return useQuery({
    queryKey: orderStatusKeys.lists(),
    queryFn: getOrderStatuses,
  })
}
export const useGetOrderReasonsQuery = () => {
  return useQuery({
    queryKey: orderStatusReasonsKeys.lists(),
    queryFn: getOrderReasons,
  })
}
export const useGetRevenueClassesQuery = () => {
  return useQuery({
    queryKey: revenueClassesKeys.lists(),
    queryFn: getRevenueClasses,
  })
}
export const useGetStatesQuery = () => {
  return useQuery({
    queryKey: stateKeys.lists(),
    queryFn: getStates,
  })
}

export const useGetContactTypesQuery = () => {
  return useQuery({
    queryKey: contactTypesKeys.lists(),
    queryFn: getContactTypes,
  })
}
export const useGetContactLanguagesQuery = () => {
  return useQuery({
    queryKey: contactLanguagesKeys.lists(),
    queryFn: getContactLanguages,
  })
}

export const useGetPhoneNumberTypesQuery = () => {
  return useQuery({
    queryKey: phoneNumberTypesKeys.lists(),
    queryFn: getPhoneNumberTypes,
  })
}

export const useGetContactDispositionsQuery = <TSelected = IContactDisposition>(
  select?: (data: IContactDisposition[]) => TSelected,
) => {
  return useQuery({
    queryKey: contactDispositionsKeys.lists(),
    queryFn: () => getContactDispositions(),
    select,
  })
}

export const useGetContactDispositionsSerializedQuery = () => {
  return useGetContactDispositionsQuery(transformContactDispositionData)
}

export const useGetDefaultDispositionsQuery = () => {
  return useQuery({
    queryKey: defaultDispositionsKeys.lists(),
    queryFn: getDefaultDispositions,
  })
}

export const useGetResubmitDispositionsQuery = () => {
  return useQuery({
    queryKey: resubmitDispositionsKeys.lists(),
    queryFn: getResubmitDispositions,
  })
}

export const useGetBusinessTitlesQuery = () => {
  return useQuery({
    queryKey: businessTitlesKeys.lists(),
    queryFn: getBusinessTitles,
  })
}

export const useGetAccountHolderRelationshipQuery = () => {
  return useQuery({
    queryKey: accountHolderRelationshipKeys.lists(),
    queryFn: getAccountHolderRelationship,
  })
}

export const useGetRejectStatusQuery = () => {
  return useQuery({
    queryKey: rejectStatusKeys.lists(),
    queryFn: () => getRejectStatus(),
    select: (data) =>
      data.map((status) => ({
        value: status.key || "__none__",
        label: status.label || "__none__",
      })),
  })
}

export const useGetCaseAssignmentGroupsQuery = () => {
  return useQuery({
    queryKey: caseAssignmentGroupsKeys.lists(),
    queryFn: getCaseAssignmentGroups,
    select: sortLabels,
  })
}
